import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../components/context/AuthProvider";
import { Button, Input, LoadingState, ErrorBoundary, Select } from "../../../components/ui";
import { useFormValidation } from "../../../hooks";
import AssetMarkdownField from "../explorer/AssetMarkdownField";
import { commonValidationRules } from "../../../utils/validation";
import { AssetTableItem, AssetDiceTemplate, AssetDiceTemplateRequest } from "../../../types/types";
import { useToast } from "../../../components/ui/Toast";
import { AssetTypeOptions } from "@/options/options";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faHouse, faXmark } from "@fortawesome/free-solid-svg-icons";
import DiceTemplateManager from "../../../components/business/DiceTemplate/DiceTemplateManager";
import { AssetService } from "../../../services/assetService";
import { DmNotesField } from "../../../components/business";

interface AssetDetailsForm {
  name: string;
  type: string;
  category: string;
  description: string;
  dmNotes: string;
}

const EditAssetPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id!);
  const auth = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [assetDetails, setAssetDetails] = useState({
    name: "",
    type: "",
    category: "",
    description: "",
    dmNotes: "",
    parentId: null as number | null,
    children: [] as AssetTableItem[],
    tags: [] as string[],
  });
  const [editingAsset, setEditingAsset] = useState(false);
  const formInitializedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableAssets, setAvailableAssets] = useState<AssetTableItem[]>([]);
  /** Full ancestor chain from root down to direct parent, used for breadcrumb. */
  const [ancestors, setAncestors] = useState<Array<{ id: number; name: string }>>([]);
  /** Current input value for adding a new tag. */
  const [tagInput, setTagInput] = useState("");

  const [sections, setSections] = useState<
    { name: string; description: string; dmNotes?: string; id?: number; isEditing?: boolean }[]
  >([]);
  // Track which section indices are collapsed (collapsed = content hidden)
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());

  // Fate Formulas — only for Beast and NPC (Character) type assets
  const [diceTemplates, setDiceTemplates] = useState<AssetDiceTemplate[]>([]);
  const [diceTemplatesLoading, setDiceTemplatesLoading] = useState(false);

  const supportsFateFormulas = (type: string) =>
    type === "Character" || type === "Beast" || type === "NPC";

  // Form validation for asset details
  const assetForm = useFormValidation<AssetDetailsForm>({
    initialValues: { name: "", type: "", category: "", description: "", dmNotes: "" },
    validationRules: {
      name: commonValidationRules.name,
      type: commonValidationRules.required,
      category: commonValidationRules.required,
      description: commonValidationRules.description,
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          ...assetDetails,
          ...values,
          id,
          parentId: assetDetails.parentId,
          tags: assetDetails.tags,
        };
        const response = await fetch(`/api/protected/assets/asset`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Network response was not ok");
        setAssetDetails({ ...assetDetails, ...values });
        setEditingAsset(false);
      } catch (error) {
        console.error("Error saving asset details:", error);
        showToast({ type: "error", message: "Failed to save asset details." });
        throw error;
      }
    },
  });
  useEffect(() => {
    setLoading(true);

    /** Recursively fetch parent assets to build [greatgrandparent, ..., parent] chain. */
    const buildAncestorChain = async (
      parentId: number | null
    ): Promise<Array<{ id: number; name: string }>> => {
      if (!parentId) return [];
      try {
        const res = await fetch(`/api/protected/assets/asset/${parentId}`, {
          headers: { Authorization: `Bearer ${auth.token()}` },
        });
        if (!res.ok) return [];
        const parent = await res.json();
        const prev = await buildAncestorChain(parent.parentId ?? null);
        return [...prev, { id: parentId, name: parent.name }];
      } catch {
        return [];
      }
    };

    fetch(`/api/protected/assets/asset/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth.token()}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then(async (data) => {
        setAssetDetails({
          name: data.name || "",
          type: data.type || "",
          category: data.category || "",
          description: data.description || "",
          dmNotes: data.dmNotes || "",
          parentId: data.parentId || null,
          children: data.children || [],
          tags: data.tags || [],
        });

        // Build the breadcrumb ancestor chain
        const chain = await buildAncestorChain(data.parentId ?? null);
        setAncestors(chain);

        setSections(data.sections || []);

        // Initialize form values only once when data is loaded
        if (!formInitializedRef.current) {
          assetForm.setValues({
            name: data.name || "",
            type: data.type || "",
            category: data.category || "",
            description: data.description || "",
            dmNotes: data.dmNotes || "",
          });
          formInitializedRef.current = true;
        }
      })
      .catch((error) => {
        console.error("Error fetching asset:", error);
        setError("Failed to load asset details. Please try again later.");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, auth]);

  // Load dice templates whenever assetDetails.type changes (and supports fate formulas)
  useEffect(() => {
    if (!supportsFateFormulas(assetDetails.type)) {
      setDiceTemplates([]);
      return;
    }
    setDiceTemplatesLoading(true);
    AssetService.getAssetDiceTemplates(id)
      .then((data) => setDiceTemplates(data ?? []))
      .catch(() => setDiceTemplates([]))
      .finally(() => setDiceTemplatesLoading(false));
  }, [id, assetDetails.type]);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await fetch("/api/protected/assets", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.token()}`,
          },
        });
        if (response.ok) {
          const assets = await response.json();
          setAvailableAssets(assets.filter((a: AssetTableItem) => a.id !== id));
        }
      } catch {
        showToast({
          type: "error",
          message: "Failed to load available assets for parent selection.",
        });
      }
    };
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, id]);

  // ── Tag helpers ──────────────────────────────────────────────────────────────
  const commitTag = (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (tag && !assetDetails.tags.includes(tag)) {
      setAssetDetails((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setAssetDetails((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleSectionChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const updatedSections = sections.map((section, i) =>
      i === index ? { ...section, [name]: value } : section
    );
    setSections(updatedSections);
  };

  const saveSection = async (index: number) => {
    const section = sections[index];
    const payload = { ...section, assetId: id };
    try {
      if (section.id) {
        // Update existing section
        await fetch(`/api/protected/assets/asset/section`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new section
        const payload = { ...section, assetId: id };
        const response = await fetch(`/api/protected/assets/asset/section`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
          body: JSON.stringify(payload),
        });
        const newSection = await response.json();
        setSections((prev) => prev.map((s, i) => (i === index ? newSection : s)));
      }
      setSections((prev) => prev.map((s, i) => (i === index ? { ...s, isEditing: false } : s)));
    } catch (error) {
      console.error("Error saving section:", error);
    }
  };

  const addSection = () => {
    setSections([...sections, { name: "", description: "", dmNotes: "", isEditing: true }]);
  };

  const deleteSection = async (index: number) => {
    const section = sections[index];
    if (section.id) {
      try {
        const res = await fetch(`/api/protected/assets/asset/section/${section.id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token()}`,
          },
        });
        if (!res.ok) throw new Error("Network response was not ok");
      } catch (error) {
        console.error("Error deleting section:", error);
        showToast({ type: "error", message: "Failed to delete section." });
        return;
      }
    }
    setSections((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelEditSection = (index: number) => {
    if (!sections[index].id) {
      // Remove unsaved section
      deleteSection(index);
    } else {
      // Revert edit mode
      setSections((prev) => prev.map((s, i) => (i === index ? { ...s, isEditing: false } : s)));
    }
  };

  const toggleSectionCollapsed = (index: number) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const collapseAllSections = () => {
    setCollapsedSections(new Set(sections.map((_, i) => i)));
  };

  const expandAllSections = () => {
    setCollapsedSections(new Set());
  };

  const handleSaveAssetDmNotes = async (dmNotes: string) => {
    try {
      const payload = {
        ...assetDetails,
        id,
        dmNotes,
        tags: assetDetails.tags,
      };
      const res = await fetch(`/api/protected/assets/asset`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token()}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Network response was not ok");
      setAssetDetails((prev) => ({ ...prev, dmNotes }));
    } catch {
      showToast({ type: "error", message: "Failed to save DM notes." });
    }
  };

  const handleSaveSectionDmNotes = async (index: number, dmNotes: string) => {
    const section = sections[index];
    if (!section) return;
    const updatedSection = { ...section, dmNotes };
    try {
      if (section.id) {
        const res = await fetch(`/api/protected/assets/asset/section`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
          body: JSON.stringify({ ...updatedSection, assetId: id }),
        });
        if (!res.ok) throw new Error("Network response was not ok");
      }
      setSections((prev) => prev.map((s, i) => (i === index ? updatedSection : s)));
    } catch {
      showToast({ type: "error", message: "Failed to save section DM notes." });
    }
  };

  if (loading) {
    return <LoadingState message="Loading asset details..." size="md" fullHeight />;
  }

  if (error) {
    return (
      <div className="p-6 bg-zinc-950 text-white min-h-screen">
        <h1 className="text-2xl font-light tracking-wide mb-6">Edit Asset</h1>
        <div className="mb-4 text-red-400">{error}</div>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 text-white min-h-screen p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Breadcrumb — full ancestor chain */}
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500 flex-wrap">
          <FontAwesomeIcon icon={faHouse} className="text-zinc-600 text-xs flex-shrink-0" />
          <button
            onClick={() => navigate("/protected/assets")}
            className="hover:text-amber-400 transition-colors ml-0.5"
          >
            Compendium
          </button>
          {ancestors.map((ancestor) => (
            <React.Fragment key={ancestor.id}>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="text-zinc-700 text-[10px] flex-shrink-0"
              />
              <button
                onClick={() => navigate(`/protected/assets/${ancestor.id}`)}
                className="hover:text-amber-400 transition-colors truncate max-w-[12rem]"
              >
                {ancestor.name}
              </button>
            </React.Fragment>
          ))}
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-zinc-700 text-[10px] flex-shrink-0"
          />
          <span className="text-zinc-300 truncate max-w-[12rem]">
            {assetDetails.name || "Edit Entry"}
          </span>
        </nav>

        <h1 className="text-2xl font-light tracking-wide">Edit Entry</h1>

        <ErrorBoundary errorMessage="There was an error with the asset editor. Please try refreshing the page.">
          {/* Asset Details */}
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase">
                Asset Details
              </h2>
              {!editingAsset && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    assetForm.setValues({
                      name: assetDetails.name,
                      type: assetDetails.type,
                      category: assetDetails.category,
                      description: assetDetails.description,
                      dmNotes: assetDetails.dmNotes,
                    });
                    setEditingAsset(true);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
            {editingAsset ? (
              <form onSubmit={assetForm.handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  name="name"
                  label="Name"
                  value={assetForm.values.name}
                  onChange={assetForm.handleChange("name")}
                  onBlur={assetForm.handleBlur("name")}
                  error={assetForm.errors.name}
                  placeholder="Asset Name"
                  fullWidth
                  variant="filled"
                />
                <Select
                  label="Type"
                  name="type"
                  value={assetForm.values.type}
                  onChange={assetForm.handleChange("type")}
                  onBlur={assetForm.handleBlur("type")}
                  error={assetForm.errors.type}
                  fullWidth
                  variant="filled"
                  options={AssetTypeOptions}
                />
                <Input
                  type="text"
                  name="category"
                  label="Category"
                  value={assetForm.values.category}
                  onChange={assetForm.handleChange("category")}
                  onBlur={assetForm.handleBlur("category")}
                  error={assetForm.errors.category}
                  placeholder="Asset Category"
                  fullWidth
                  variant="filled"
                />
                <Select
                  label="Parent Asset (Optional)"
                  name="parentId"
                  value={assetDetails.parentId ? assetDetails.parentId.toString() : ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : parseInt(e.target.value);
                    setAssetDetails((prev) => ({ ...prev, parentId: val }));
                  }}
                  options={[
                    { value: "", label: "No Parent (Root Entry)" },
                    ...availableAssets.map((asset) => ({
                      value: asset.id.toString(),
                      label: `${asset.name} (${asset.type})`,
                    })),
                  ]}
                  fullWidth
                  variant="filled"
                />

                {/* Tags editor */}
                <div>
                  <label className="block text-sm text-amber-400 tracking-wide mb-2">Tags</label>
                  {assetDetails.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {assetDetails.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-800 border border-zinc-700 text-xs text-zinc-300 rounded-full"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="text-zinc-500 hover:text-red-400 transition-colors"
                          >
                            <FontAwesomeIcon icon={faXmark} className="text-[9px]" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        commitTag(tagInput);
                      } else if (
                        e.key === "Backspace" &&
                        tagInput === "" &&
                        assetDetails.tags.length > 0
                      ) {
                        removeTag(assetDetails.tags[assetDetails.tags.length - 1]);
                      }
                    }}
                    onBlur={() => tagInput.trim() && commitTag(tagInput)}
                    placeholder="Type a tag and press Enter or comma…"
                    className="w-full bg-zinc-800/60 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                  />
                  <p className="text-xs text-zinc-600 mt-1">
                    Press Enter or , to add · Backspace to remove last
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-amber-400 tracking-wide mb-2">
                    Description
                  </label>
                  <AssetMarkdownField
                    value={assetForm.values.description}
                    allAssets={availableAssets}
                    onReferenceNavigate={(assetId) => navigate(`/protected/assets/${assetId}`)}
                    onChange={(val) =>
                      assetForm.handleChange("description")({
                        target: { name: "description", value: val },
                      } as React.ChangeEvent<HTMLTextAreaElement>)
                    }
                  />
                  {assetForm.errors.description && (
                    <div className="text-red-400 text-xs mt-1">{assetForm.errors.description}</div>
                  )}
                </div>

                {/* DM Notes — private, never shared */}
                <div>
                  <label className="block text-sm text-amber-400 tracking-wide mb-1">
                    🔒 DM Notes
                    <span className="text-zinc-500 font-normal ml-2 text-xs">
                      Private — never shared with players
                    </span>
                  </label>
                  <textarea
                    name="dmNotes"
                    value={assetForm.values.dmNotes}
                    onChange={(e) =>
                      assetForm.handleChange("dmNotes")({
                        target: { name: "dmNotes", value: e.target.value },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    rows={3}
                    placeholder="Notes visible only to you…"
                    className="w-full bg-amber-900/10 border border-amber-800/30 rounded px-3 py-2 text-sm text-amber-200/80 placeholder-amber-700/50 focus:outline-none focus:border-amber-600/50 resize-y"
                  />
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={assetForm.isSubmitting}
                    loading={assetForm.isSubmitting}
                  >
                    {assetForm.isSubmitting ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      assetForm.setValues({
                        name: assetDetails.name,
                        type: assetDetails.type,
                        category: assetDetails.category,
                        description: assetDetails.description,
                        dmNotes: assetDetails.dmNotes,
                      });
                      assetForm.resetForm();
                      setEditingAsset(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-2">
                <p className="text-zinc-300">
                  <span className="text-amber-400/70 text-xs tracking-widest uppercase mr-2">
                    Name
                  </span>
                  {assetDetails.name}
                </p>
                <p className="text-zinc-300">
                  <span className="text-amber-400/70 text-xs tracking-widest uppercase mr-2">
                    Type
                  </span>
                  {assetDetails.type}
                </p>
                <p className="text-zinc-300">
                  <span className="text-amber-400/70 text-xs tracking-widest uppercase mr-2">
                    Category
                  </span>
                  {assetDetails.category}
                </p>
                {assetDetails.tags.length > 0 && (
                  <div>
                    <span className="text-amber-400/70 text-xs tracking-widest uppercase block mb-1.5">
                      Tags
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {assetDetails.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 bg-zinc-800 text-xs text-zinc-300 rounded-full border border-zinc-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-amber-400/70 text-xs tracking-widest uppercase block mb-1">
                    Description
                  </span>
                  <AssetMarkdownField
                    readOnly={true}
                    value={assetDetails.description}
                    allAssets={availableAssets}
                    onReferenceNavigate={(assetId) => navigate(`/protected/assets/${assetId}`)}
                  />
                </div>
                <DmNotesField value={assetDetails.dmNotes ?? ""} onSave={handleSaveAssetDmNotes} />
              </div>
            )}
          </div>

          {/* Parent/Child Navigation */}
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase mb-4">
              Hierarchy
            </h2>
            {assetDetails.parentId && (
              <div className="mb-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Parent</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/protected/assets/${assetDetails.parentId}`)}
                >
                  ↑ Edit Parent Asset
                </Button>
              </div>
            )}
            {assetDetails.children && assetDetails.children.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">
                  Children ({assetDetails.children.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {assetDetails.children.map((child: AssetTableItem) => (
                    <Button
                      key={child.id}
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/protected/assets/${child.id}`)}
                    >
                      ↓ {child.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/protected/assets/create?parentId=${id}`)}
              >
                + Add Child Asset
              </Button>
            </div>
            {!assetDetails.parentId &&
              (!assetDetails.children || assetDetails.children.length === 0) && (
                <p className="text-zinc-500 text-sm">This asset has no parent or child assets.</p>
              )}
          </div>

          {/* Sections */}
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xs font-semibold text-amber-400 tracking-widest uppercase">
                Sections
              </h2>
              {sections.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={expandAllSections}
                    className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                  >
                    Expand All
                  </button>
                  <span className="text-zinc-700">|</span>
                  <button
                    type="button"
                    onClick={collapseAllSections}
                    className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                  >
                    Collapse All
                  </button>
                </div>
              )}
            </div>
            {sections.map((section, index) => {
              const isCollapsed = collapsedSections.has(index);
              return (
                <div
                  key={index}
                  className="border border-zinc-800 rounded-md overflow-hidden mb-3 last:mb-0"
                >
                  {/* Section header row */}
                  <div className="flex items-center px-3 py-2 bg-zinc-900/70 gap-2">
                    <button
                      type="button"
                      onClick={() => toggleSectionCollapsed(index)}
                      className="flex items-center gap-2 flex-1 text-left min-w-0"
                    >
                      <FontAwesomeIcon
                        icon={isCollapsed ? faChevronRight : faChevronDown}
                        className="text-zinc-500 text-[10px] flex-shrink-0"
                      />
                      {!section.isEditing ? (
                        <h3 className="text-base font-medium text-white truncate">
                          {section.name || (
                            <span className="text-zinc-500 italic">Untitled section</span>
                          )}
                        </h3>
                      ) : (
                        <span className="text-sm text-zinc-400 italic truncate">Editing…</span>
                      )}
                    </button>
                    <div className="flex space-x-2 ml-2 flex-shrink-0">
                      {!section.isEditing && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setSections((prev) =>
                              prev.map((s, i) => (i === index ? { ...s, isEditing: true } : s))
                            )
                          }
                        >
                          Edit
                        </Button>
                      )}
                      <Button variant="danger" size="sm" onClick={() => deleteSection(index)}>
                        Delete
                      </Button>
                    </div>
                  </div>

                  {/* Section body — hidden when collapsed */}
                  {!isCollapsed && (
                    <div className="px-3 py-3 bg-zinc-950/40 space-y-2">
                      {section.isEditing && (
                        <Input
                          type="text"
                          name="name"
                          value={section.name}
                          onChange={(e) => handleSectionChange(index, e)}
                          fullWidth
                          variant="filled"
                        />
                      )}
                      {section.isEditing ? (
                        <AssetMarkdownField
                          value={section.description}
                          allAssets={availableAssets}
                          onReferenceNavigate={(assetId) =>
                            navigate(`/protected/assets/${assetId}`)
                          }
                          onChange={(val) =>
                            handleSectionChange(index, {
                              target: { name: "description", value: val },
                            } as React.ChangeEvent<HTMLTextAreaElement>)
                          }
                        />
                      ) : (
                        <div className="mt-2 bg-zinc-800/40 border border-zinc-800 rounded p-3">
                          <AssetMarkdownField
                            readOnly={true}
                            value={section.description}
                            allAssets={availableAssets}
                            onReferenceNavigate={(assetId) =>
                              navigate(`/protected/assets/${assetId}`)
                            }
                          />
                        </div>
                      )}
                      {/* DM Notes for section */}
                      {section.isEditing ? (
                        <div>
                          <label className="block text-xs text-amber-500/70 tracking-widest uppercase mb-1">
                            🔒 DM Notes
                            <span className="text-zinc-500 font-normal ml-2 normal-case">
                              Private — never shared
                            </span>
                          </label>
                          <textarea
                            name="dmNotes"
                            value={section.dmNotes ?? ""}
                            onChange={(e) => handleSectionChange(index, e)}
                            rows={2}
                            placeholder="Notes visible only to you…"
                            className="w-full bg-amber-900/10 border border-amber-800/30 rounded px-3 py-2 text-sm text-amber-200/80 placeholder-amber-700/50 focus:outline-none focus:border-amber-600/50 resize-y"
                          />
                        </div>
                      ) : (
                        <DmNotesField
                          value={section.dmNotes ?? ""}
                          onSave={(dmNotes) => handleSaveSectionDmNotes(index, dmNotes)}
                        />
                      )}
                      {section.isEditing && (
                        <div className="flex space-x-3">
                          <Button variant="primary" size="sm" onClick={() => saveSection(index)}>
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelEditSection(index)}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="mt-3">
              <Button variant="secondary" size="sm" onClick={addSection}>
                + Add Section
              </Button>
            </div>
          </div>

          {/* Fate Formulas — Beast & NPC entries only */}
          {supportsFateFormulas(assetDetails.type) && (
            <div className="mt-6">
              <DiceTemplateManager
                templates={diceTemplates.map((t) => ({
                  id: t.id,
                  characterId: t.assetId,
                  name: t.name,
                  diceNotation: t.diceNotation,
                  modifier: t.modifier,
                  description: t.description,
                  skillType: t.skillType,
                  createdAt: t.createdAt,
                  updatedAt: t.updatedAt,
                }))}
                loading={diceTemplatesLoading}
                onCreateTemplate={async (req: AssetDiceTemplateRequest) => {
                  const created = await AssetService.createAssetDiceTemplate(id, req);
                  setDiceTemplates((prev) => [...prev, created]);
                }}
                onUpdateTemplate={async (templateId: number, req: AssetDiceTemplateRequest) => {
                  const updated = await AssetService.updateAssetDiceTemplate(id, templateId, req);
                  setDiceTemplates((prev) => prev.map((t) => (t.id === templateId ? updated : t)));
                }}
                onDeleteTemplate={async (templateId: number) => {
                  await AssetService.deleteAssetDiceTemplate(id, templateId);
                  setDiceTemplates((prev) => prev.filter((t) => t.id !== templateId));
                }}
              />
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default EditAssetPage;
